import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Upload, Pencil, FileText, Library, RefreshCw, FolderTree, Sparkles, ChevronRight, Shield, Layers, Users } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { LuminexIcon } from '../components/LuminexLogo';
import HeroAtlasIllustration from '../components/HeroAtlasIllustration';
import PublicHeader from '../components/PublicHeader';

/* ═════════ ANIMATED COUNTER (reused) ═════════ */
function AnimatedCounter({ target, duration = 1800 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const observed = useRef(false);
  useEffect(() => {
    const num = parseInt(String(target).replace(/[^0-9]/g, ''));
    if (isNaN(num)) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !observed.current) {
        observed.current = true;
        const start = performance.now();
        const step = (t) => {
          const p = Math.min((t - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setCount(Math.floor(eased * num));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration]);
  const suffix = String(target).replace(/[0-9,]/g, '');
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

/* ═════════ INDUSTRY COVERAGE (matches real LOMs) ═════════ */
const INDUSTRIES = [
  'Oil, Gas & Energy', 'Power & Utilities', 'Chemicals', 'Mining, Metals & Minerals',
  'Infrastructure', 'Manufacturing', 'Marine', 'Data Centers', 'Pharmaceuticals',
  'Semiconductors', 'Aerospace & Defense', 'Water & Wastewater', 'Renewables',
  'LNG & Gas Processing', 'Petrochemicals', 'Battery & BESS', 'Hydrogen', 'Nuclear',
  'Grid & Transmission', 'Combined Cycle', 'Steel & Iron', 'Cement', 'Pulp & Paper',
  'Ports & Terminals', 'Rail', 'Bridges & Tunnels', 'Highways', 'Airports', 'Telecom', 'Automotive',
];

/* ═════════ THE THREE ACTS (actual value loop) ═════════ */
const ACTS = [
  {
    no: '01', k: 'Extract', icon: Upload,
    title: 'From legacy document to swimlane, in under a minute.',
    body: 'Drop a PDF, scanned SOP, DOCX, PPTX, or BPMN diagram. Generative AI (with OCR fallback for scanned pages) reads the document end-to-end and structures the process into actors, steps, RACI, KPIs, and escalation — ready to edit.',
  },
  {
    no: '02', k: 'Optimize', icon: Pencil,
    title: 'Edit the as-is into the to-be on an enterprise-grade canvas.',
    body: 'Rearrange steps in a 4-tier swimlane editor: collapse handoffs, eliminate redundant approvals, consolidate actors, re-sequence parallel work. Save as a reviewable "Optimized" snapshot alongside the original extraction.',
  },
  {
    no: '03', k: 'Deliver', icon: FileText,
    title: 'Generate a steering-committee-ready savings deliverable.',
    body: 'One click produces the Re-engineering Delta Report: steps eliminated, handoffs reduced, cycle-time compression, FTE-hour savings, RACI simplifications, risk flags, and prioritized recommendations — exportable as PDF.',
  },
];

/* ═════════ PRODUCT MODULES ═════════ */
const MODULES = [
  { icon: Library, title: 'Logical Operating Models', desc: '30 industry verticals. L0→L4 hierarchy. 7,400+ pre-built business processes with swimlane deep-dives.', path: '/library', tid: 'module-card-library' },
  { icon: FolderTree, title: 'Process Creation', desc: 'Replicate any L1→L2→L3 hierarchy into your own tenant. Attach BPs at leaf level. Edit replicated processes inline.', path: '/folders', tid: 'module-card-folders' },
  { icon: RefreshCw, title: 'Process Re-engineering', desc: 'Upload legacy docs → AI extraction → edit → Delta Report. OCR fallback for scanned procedures. Before/after comparison view.', path: '/reengineer', tid: 'module-card-reengineer' },
  { icon: Sparkles, title: 'AI-Assisted Authoring', desc: 'Draft custom BPs from natural language with Generative AI. Maps directly into the full deep-dive editor — no blank page problem.', path: '/folders', tid: 'module-card-ai' },
];

/* ═════════ CAPABILITIES ═════════ */
const CAPABILITIES = [
  { icon: Layers, title: '4-Tier Hierarchy', desc: 'Domain → Function → Sub-Function → Business Process, with swimlane rendering at L4.' },
  { icon: Users, title: 'RACI & Actors', desc: '800+ pre-mapped actors (roles, OEMs, regulators). Cross-industry actor registry.' },
  { icon: Shield, title: 'Compliance-Ready', desc: 'Pre-baked mappings to NERC, FERC, IEEE, NFPA, OSHA, EPA, AWWA, IEC, and 40+ frameworks.' },
  { icon: FileText, title: 'Printable Deliverables', desc: 'Delta Reports export to PDF with print CSS — executive-grade handoff to steering committees.' },
];

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  return (
    <div data-testid="home-page" style={{ background: 'var(--bg-0)', minHeight: '100vh' }}>
      <PublicHeader />
      <style>{`
        @keyframes vd-fade-up { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes vd-spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .vd-fade-up { animation: vd-fade-up 0.6s ease-out both; }
        .vd-card { transition: transform 0.25s ease, border-color 0.25s ease, background 0.25s ease; }
        .vd-card:hover { transform: translateY(-3px); border-color: var(--accent) !important; }
        .vd-industry-chip { transition: all 0.2s ease; }
        .vd-industry-chip:hover { background: var(--accent-dim); border-color: var(--accent); color: var(--accent); }
        @media (max-width: 900px) {
          .vd-hero { padding: 56px 24px 48px !important; grid-template-columns: 1fr !important; }
          .vd-acts { grid-template-columns: 1fr !important; }
          .vd-modules, .vd-capabilities { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ═══════════ HERO — editorial, asymmetric, obsidian left + content right ═══════════ */}
      <section style={{
        position: 'relative',
        background: 'var(--gradient-obsidian)',
        color: '#F3F4F6',
        overflow: 'hidden',
      }}>
        {/* Ember glow */}
        <div style={{ position: 'absolute', top: '-10%', right: '-5%', width: '60%', height: '80%', background: 'radial-gradient(ellipse, rgba(242,107,31,0.14), transparent 60%)', pointerEvents: 'none' }} />
        {/* Background diamond */}
        <div style={{ position: 'absolute', top: '50%', left: '55%', transform: 'translate(-50%,-50%)', opacity: 0.04, color: '#5790C9', pointerEvents: 'none' }}>
          <LuminexIcon size={900} />
        </div>
        {/* Grain */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='a'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23a)' opacity='0.4'/%3E%3C/svg%3E\")", opacity: 0.04, mixBlendMode: 'overlay', pointerEvents: 'none' }} />

        <div className="vd-hero" style={{
          position: 'relative', zIndex: 2,
          maxWidth: 1280, margin: '0 auto',
          padding: '96px 48px 88px',
          display: 'grid',
          gridTemplateColumns: '1.1fr 1fr',
          gap: 64,
          alignItems: 'center',
        }}>
          <div className="vd-fade-up">
            {user && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '6px 14px', borderRadius: 100, border: '1px solid rgba(87,144,201,0.3)', background: 'rgba(87,144,201,0.08)', fontSize: 12, color: '#8FB3DE', marginBottom: 28 }} data-testid="welcome-badge">
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#F26B1F' }} />
                Welcome back, <span style={{ color: '#F3F4F6', fontWeight: 600 }}>{user.firstName}</span>
                <span style={{ color: 'rgba(243,244,246,0.3)' }}>·</span>
                <span style={{ color: '#F26B1F', fontWeight: 700 }}>{user.tokens}</span> tokens
              </div>
            )}
            <div style={{ fontSize: 11, fontWeight: 700, color: '#F26B1F', textTransform: 'uppercase', letterSpacing: 2.4, marginBottom: 20 }}>
              Lumine<span style={{ color: '#F26B1F' }}>x</span> · Process Intelligence for Capital Projects
            </div>
            <h1 data-testid="hero-title" style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2.6rem, 5.2vw, 4.4rem)',
              fontWeight: 600,
              lineHeight: 1.05,
              letterSpacing: '-0.025em',
              color: '#F3F4F6',
              marginBottom: 24,
            }}>
              Process intelligence<br />
              for <span style={{ color: '#F26B1F', fontStyle: 'italic' }}>capital projects</span>.
            </h1>
            <p style={{ fontSize: 17, lineHeight: 1.65, color: 'rgba(243,244,246,0.75)', maxWidth: 560, marginBottom: 36 }}>
              Upload a legacy procedure. Luminex extracts the as-is swimlane, lets you re-engineer it, and delivers a steering-committee-grade savings report with quantified cycle-time, FTE, and RACI deltas. 30 industries, 7,400+ pre-built business processes, L0→L4 hierarchy.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/library')} data-testid="browse-library-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 24px', borderRadius: 8, background: '#1E4E8C', color: '#E6EEF9', border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseOver={(e) => e.currentTarget.style.background = '#143A6B'}
                onMouseOut={(e) => e.currentTarget.style.background = '#1E4E8C'}>
                Browse Operating Models <ArrowRight size={16} />
              </button>
              <button onClick={() => navigate('/reengineer')} data-testid="start-reeng-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 24px', borderRadius: 8, background: 'transparent', color: '#F3F4F6', border: '1px solid rgba(243,244,246,0.25)', fontSize: 14, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseOver={(e) => { e.currentTarget.style.borderColor = '#5790C9'; e.currentTarget.style.background = 'rgba(87,144,201,0.08)'; }}
                onMouseOut={(e) => { e.currentTarget.style.borderColor = 'rgba(243,244,246,0.25)'; e.currentTarget.style.background = 'transparent'; }}>
                <Upload size={16} /> Start Process Re-engineering
              </button>
            </div>
          </div>

          {/* Right column: Capital Projects Atlas — radial L0→L4 × 30 industries */}
          <div className="vd-fade-up" style={{ animationDelay: '120ms', position: 'relative' }}>
            <HeroAtlasIllustration />
          </div>
        </div>
      </section>

      {/* ═══════════ THE THREE ACTS ═══════════ */}
      <section style={{ maxWidth: 1280, margin: '0 auto', padding: '88px 48px 72px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 2.4, marginBottom: 14 }}>
          The Luminex Loop
        </div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.8rem, 3vw, 2.6rem)', fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--text-1)', maxWidth: 720, marginBottom: 56, lineHeight: 1.2 }}>
          Document-in, savings-out. Three acts, roughly five minutes per process.
        </h2>

        <div className="vd-acts" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 28 }}>
          {ACTS.map((a, i) => {
            const Icon = a.icon;
            return (
              <div key={a.no} style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 20, borderTop: '2px solid var(--text-1)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 40, fontWeight: 600, color: 'var(--accent)', lineHeight: 1 }}>{a.no}</span>
                  <span style={{ color: 'var(--accent)' }}><Icon size={22} /></span>
                  <span style={{ fontSize: 11, fontWeight: 800, color: '#F26B1F', textTransform: 'uppercase', letterSpacing: 1.5 }}>{a.k}</span>
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 600, color: 'var(--text-1)', lineHeight: 1.3, letterSpacing: '-0.01em' }}>{a.title}</h3>
                <p style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--text-2)' }}>{a.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══════════ STATS STRIP — obsidian band ═══════════ */}
      <section style={{ background: 'var(--gradient-obsidian)', color: '#F3F4F6', padding: '56px 48px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, width: '40%', height: '100%', background: 'radial-gradient(ellipse at left, rgba(242,107,31,0.1), transparent 60%)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 32, position: 'relative' }}>
          {[['30', 'Industry verticals'], ['7,400+', 'Pre-built BPs'], ['800+', 'Mapped actors'], ['L4', 'Swimlane depth']].map(([v, l]) => (
            <div key={l}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 46, fontWeight: 600, color: '#F3F4F6', letterSpacing: '-0.02em', lineHeight: 1 }}>
                <AnimatedCounter target={v} />
              </div>
              <div style={{ fontSize: 12, color: 'rgba(243,244,246,0.6)', textTransform: 'uppercase', letterSpacing: 1.2, marginTop: 8 }}>{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════ MODULES ═══════════ */}
      <section style={{ maxWidth: 1280, margin: '0 auto', padding: '88px 48px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 40, flexWrap: 'wrap', gap: 20 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 2.4, marginBottom: 12 }}>Platform Modules</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.8rem, 3vw, 2.6rem)', fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--text-1)', maxWidth: 700, lineHeight: 1.2 }}>
              Four surfaces, one operating model discipline.
            </h2>
          </div>
        </div>

        <div className="vd-modules" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>
          {MODULES.map((m) => {
            const Icon = m.icon;
            return (
              <div key={m.title} className="vd-card" onClick={() => navigate(m.path)} data-testid={m.tid}
                style={{ cursor: 'pointer', padding: '28px 28px 26px', border: '1px solid var(--border-1)', borderRadius: 12, background: 'var(--bg-1)', position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: 10, background: 'var(--accent-dim)', color: 'var(--accent)' }}>
                    <Icon size={20} />
                  </span>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 19, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>{m.title}</h3>
                </div>
                <p style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--text-2)', marginBottom: 16 }}>{m.desc}</p>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>
                  Open module <ChevronRight size={14} />
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══════════ INDUSTRY MARQUEE ═══════════ */}
      <section style={{ background: 'var(--bg-2)', borderTop: '1px solid var(--border-1)', borderBottom: '1px solid var(--border-1)', padding: '72px 48px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 2.4, marginBottom: 14 }}>Industry Coverage</div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.6rem, 2.6vw, 2.2rem)', fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--text-1)', maxWidth: 760, marginBottom: 30, lineHeight: 1.2 }}>
            Thirty capital-intensive verticals, each with their own L0 → L4 LOM.
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {INDUSTRIES.map((ind) => (
              <span key={ind} className="vd-industry-chip" onClick={() => navigate('/library')}
                style={{ padding: '7px 14px', borderRadius: 100, background: 'var(--bg-1)', border: '1px solid var(--border-1)', color: 'var(--text-2)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                {ind}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ CAPABILITIES ═══════════ */}
      <section style={{ maxWidth: 1280, margin: '0 auto', padding: '88px 48px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 2.4, marginBottom: 14 }}>Capabilities</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.8rem, 3vw, 2.4rem)', fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--text-1)', maxWidth: 720, marginBottom: 56, lineHeight: 1.2 }}>
          Purpose-built for consulting, PMO, and steering-committee-grade delivery.
        </h2>

        <div className="vd-capabilities" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24 }}>
          {CAPABILITIES.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.title}>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 8, background: 'var(--accent-dim)', color: 'var(--accent)', marginBottom: 14 }}>
                  <Icon size={18} />
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text-1)', marginBottom: 6, letterSpacing: '-0.01em' }}>{c.title}</div>
                <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6 }}>{c.desc}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══════════ FINAL CTA ═══════════ */}
      <section style={{ background: 'var(--gradient-obsidian)', color: '#F3F4F6', padding: '88px 48px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-30%', right: '-10%', width: '60%', height: '160%', background: 'radial-gradient(ellipse, rgba(242,107,31,0.18), transparent 60%)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 2 }}>
          <div style={{ color: '#5790C9', display: 'inline-block', marginBottom: 26 }}>
            <LuminexIcon size={54} />
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.02em', color: '#F3F4F6', marginBottom: 20 }}>
            Start with an existing process,<br />
            <span style={{ color: '#F26B1F', fontStyle: 'italic' }}>end with a savings deliverable</span>.
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.65, color: 'rgba(243,244,246,0.7)', maxWidth: 620, margin: '0 auto 32px' }}>
            Luminex = ordering, systematic arrangement. Bring the discipline of a 4-tier operating model to every capital project on your portfolio.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate(user ? '/reengineer' : '/register')} data-testid="final-cta-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 26px', borderRadius: 8, background: '#1E4E8C', color: '#E6EEF9', border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              {user ? 'Open Process Re-engineering' : 'Create your workspace'} <ArrowRight size={16} />
            </button>
            <button onClick={() => navigate('/pricing')} data-testid="final-cta-pricing"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 26px', borderRadius: 8, background: 'transparent', color: '#F3F4F6', border: '1px solid rgba(243,244,246,0.25)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              View pricing
            </button>
          </div>
        </div>
      </section>

      {/* ═══════════ FOOTER ═══════════ */}
      <footer style={{ padding: '28px 48px', background: 'var(--bg-1)', borderTop: '1px solid var(--border-1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: 'var(--text-2)', fontSize: 12 }}>
          <span style={{ color: 'var(--accent)' }}><LuminexIcon size={18} /></span>
          <span style={{ fontFamily: '"Playfair Display", Georgia, serif', fontStyle: 'italic', fontSize: 14, letterSpacing: '-0.01em' }}>
            Lumine<span style={{ color: '#F26B1F' }}>x</span>
          </span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
          Process Intelligence for Capital Projects
        </div>
      </footer>
    </div>
  );
}
